import { useEffect, useRef, useState } from 'react';

import { Loader2, Plus, Search } from 'lucide-react';

import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/Dialog';
import { Image } from '../../../components/ui/Image';
import { Input } from '../../../components/ui/Input';
import { Routes } from '../../../models/routes';
import { csrfTokenHeader } from '../../../services/CsrfTokenService';
import { CartonProduct } from '../../../types';

type Props = {
    collectionId: number | null;
    collectionName?: string;
    existingProductIds: number[];
    onAdded: () => void;
};

const SEARCH_DELAY_MS = 300;
const MIN_QUERY_LENGTH = 3;

const responseError = async (response: Response) => {
    const payload = await response.json().catch(() => null);
    return payload?.errors
        ? Object.values(payload.errors).join(' ')
        : 'Не вдалося додати товар до списку бажаного';
};

export const WishlistProductSearchCard = ({
    collectionId,
    collectionName,
    existingProductIds,
    onAdded,
}: Props) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [products, setProducts] = useState<CartonProduct[]>([]);
    const [searching, setSearching] = useState(false);
    const [addingProductId, setAddingProductId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const normalizedQuery = query.trim();

    useEffect(() => {
        if (!open || normalizedQuery.length < MIN_QUERY_LENGTH) {
            abortControllerRef.current?.abort();
            setProducts([]);
            setSearching(false);
            return;
        }

        setSearching(true);
        setError(null);
        const abortController = new AbortController();
        abortControllerRef.current?.abort();
        abortControllerRef.current = abortController;

        const timeoutId = window.setTimeout(async () => {
            try {
                const response = await fetch(
                    `${Routes.ProductLookup}?q=${encodeURIComponent(normalizedQuery)}`,
                    { signal: abortController.signal },
                );
                if (!response.ok)
                    throw new Error(await responseError(response));

                const payload = (await response.json()) as CartonProduct[];
                if (!abortController.signal.aborted) setProducts(payload);
            } catch (requestError) {
                if (!abortController.signal.aborted) {
                    setProducts([]);
                    setError((requestError as Error).message);
                }
            } finally {
                if (!abortController.signal.aborted) setSearching(false);
            }
        }, SEARCH_DELAY_MS);

        return () => {
            window.clearTimeout(timeoutId);
            abortController.abort();
        };
    }, [normalizedQuery, open]);

    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) {
            setQuery('');
            setProducts([]);
            setError(null);
        }
    };

    const addProduct = async (productId: number) => {
        setAddingProductId(productId);
        setError(null);
        try {
            const response = await fetch(Routes.WishlistItems, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...csrfTokenHeader(),
                },
                body: JSON.stringify({
                    product_id: productId,
                    collection_ids: collectionId ? [collectionId] : [],
                }),
            });
            if (!response.ok) throw new Error(await responseError(response));

            setOpen(false);
            setQuery('');
            setProducts([]);
            onAdded();
        } catch (requestError) {
            setError((requestError as Error).message);
        } finally {
            setAddingProductId(null);
        }
    };

    return (
        <>
            <button
                type="button"
                className="group/add flex h-full flex-col overflow-hidden rounded-2xl border border-dashed bg-muted/10 text-muted-foreground transition-colors hover:border-primary/60 hover:bg-primary/5 hover:text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none"
                onClick={() => setOpen(true)}
                data-testid="wishlist-add-product-card"
                aria-label="Додати товар до списку бажаного"
            >
                <div className="flex aspect-[3/4] w-full items-center justify-center bg-muted/20 transition-colors group-hover/add:bg-primary/5">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full border border-dashed bg-background/80 shadow-sm">
                        <Plus className="h-7 w-7" />
                    </span>
                </div>
                <div className="w-full p-2 sm:p-3">
                    <div className="flex h-20 items-center justify-center text-sm font-medium">
                        Додати товар
                    </div>
                </div>
            </button>

            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Додати товар</DialogTitle>
                        <DialogDescription>
                            {collectionName
                                ? `Знайдіть товар і додайте його до колекції «${collectionName}».`
                                : 'Знайдіть товар і додайте його до списку бажаного.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="relative">
                        <Search className="absolute top-2.5 left-3 h-4 w-4 text-muted-foreground" />
                        <Input
                            type="search"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Назва або номер товару"
                            aria-label="Пошук товару"
                            className="pl-9"
                            autoFocus
                        />
                    </div>

                    <div className="max-h-[50vh] space-y-2 overflow-y-auto">
                        {searching ? (
                            <div className="flex justify-center py-8">
                                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            </div>
                        ) : normalizedQuery.length < MIN_QUERY_LENGTH ? (
                            <p className="py-8 text-center text-sm text-muted-foreground">
                                Введіть щонайменше 3 символи.
                            </p>
                        ) : products.length > 0 ? (
                            products.map((product) => {
                                const alreadyAdded =
                                    existingProductIds.includes(product.id) ||
                                    (!collectionId && product.wishlisted);

                                return (
                                    <div
                                        key={product.id}
                                        className="flex items-center gap-3 rounded-xl border p-2"
                                        data-testid={`wishlist-product-search-result-${product.id}`}
                                    >
                                        <Image
                                            src={product.image}
                                            alt={product.image_alt}
                                            className="h-16 w-12 shrink-0 rounded-md object-cover"
                                            loading="lazy"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="line-clamp-2 text-sm font-medium">
                                                {product.name}
                                            </p>
                                            <div className="mt-1.5 flex flex-wrap gap-1">
                                                <Badge className="text-xs">
                                                    {product.expansion_tag}
                                                </Badge>
                                                <Badge
                                                    variant="outline"
                                                    className="text-xs"
                                                >
                                                    {product.language}
                                                </Badge>
                                            </div>
                                        </div>
                                        <Button
                                            type="button"
                                            size="sm"
                                            className="shrink-0"
                                            disabled={
                                                alreadyAdded ||
                                                addingProductId !== null
                                            }
                                            onClick={() =>
                                                void addProduct(product.id)
                                            }
                                        >
                                            {alreadyAdded
                                                ? 'Додано'
                                                : addingProductId === product.id
                                                  ? 'Додавання…'
                                                  : 'Додати'}
                                        </Button>
                                    </div>
                                );
                            })
                        ) : error ? null : (
                            <p className="py-8 text-center text-sm text-muted-foreground">
                                Товари не знайдено.
                            </p>
                        )}
                    </div>

                    {error && (
                        <p className="text-sm text-destructive" role="alert">
                            {error}
                        </p>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
